import type {
  BuscaDataFimTrialAssistProOutput,
  FluxoPendenteDoUsuarioType,
  FuncionalidadeContratadaOutput,
  Maybe,
  PacoteServicoOutput,
  UserQueryQuery,
  UsuarioAcaoPromocionalGraphQlType,
} from "@repo/graphql-types/graphql";

import { EMPTY_TRIAL_ASSIST_PRO } from "@/lib/constants/subscribe-assist-pro";
import { LivanceApiPaymentMethod } from "@/lib/enums/livance-api-payment-methods-enum";
import { convertDateTimeIgnoringTimezone } from "@/lib/helpers/date";
import type { UserContext } from "@/lib/interfaces/user-context";

const COD_PORTO_SEGURO = 1;

interface MapToUserContextParams {
  userQuery: UserQueryQuery | undefined;
  hasRecipientAccount: boolean;
  sub: string;
}

export const mapToUserContext = ({
  userQuery,
  hasRecipientAccount,
  sub,
}: MapToUserContextParams): UserContext => {
  const user = userQuery?.usuarios[0];
  const clinic = userQuery?.clinicas[0];
  const integrations = userQuery?.integracoes ?? [];
  const member = userQuery?.membro;

  const possivelDataFimTrialAssistPro: BuscaDataFimTrialAssistProOutput =
    (member?.possivelDataFimTrialAssistPro as Maybe<BuscaDataFimTrialAssistProOutput>) ??
    EMPTY_TRIAL_ASSIST_PRO;

  const lastTermsOfUseAgreed = user?.userTermsOfUse.length
    ? user.userTermsOfUse[0]
    : undefined;

  const validationDate = user?.usuarioProfissional?.dataValidacao
    ? convertDateTimeIgnoringTimezone(user.usuarioProfissional.dataValidacao)
    : undefined;

  const dateStartDebt = user?.usuarioProfissional?.dataInicioDivida
    ? new Date(user.usuarioProfissional.dataInicioDivida)
    : undefined;

  const funcionalidadesContratadas: FuncionalidadeContratadaOutput[] =
    (member?.funcionalidadesContratadas ?? []) as FuncionalidadeContratadaOutput[];

  const pacotesServicos: PacoteServicoOutput[] = (member?.pacotesServicos ??
    []) as PacoteServicoOutput[];

  const ramalTroncoNormalizado =
    user?.usuarioProfissional?.usuarioTelefone?.ramalTroncoNormalizado ?? "";

  const acoesPromocionais: UsuarioAcaoPromocionalGraphQlType[] =
    (member?.acoesPromocionais ?? []) as UsuarioAcaoPromocionalGraphQlType[];

  const userDomains = userQuery?.usuariosDominiosRegistros ?? [];
  const hasCustomSite =
    userDomains.length > 0 || Boolean(user?.usuarioProfissional?.dominioCriado);

  const cardPaymentType = LivanceApiPaymentMethod.CreditCard.valueOf();
  const possuiCartaoPendente =
    user?.usuarioProfissional?.formaDePagamento === cardPaymentType &&
    user.usuarioProfissional.cobrancaAtiva &&
    !user.cartoes.length;

  return {
    apelido: user?.apelido,
    ativo: user?.ativo ?? false,
    nome: user?.nome ?? "",
    sobrenome: user?.sobrenome ?? "",
    cpf: user?.cpf ?? "",
    codUsuario: user?.codUsuario ?? 0,
    codPerfil: user?.codPerfil ?? 0,
    codConselhoClasse: user?.usuarioProfissional?.codConselhoClasse ?? "",
    codClinica: clinic?.codClinica,
    pj: user?.usuarioProfissional?.pj ?? false,
    email: user?.email,
    bloqueado: user?.usuarioProfissional?.bloqueado ?? false,
    statusDocumentosCaf: user?.usuarioProfissional?.statusDocumentosCaf ?? null,
    dataInicioDivida: dateStartDebt,
    dataValidacao: validationDate,
    codParceiroPortoSeguro: integrations.some(
      (integration) => integration.codParceiro === COD_PORTO_SEGURO,
    ),
    modalidadesDeParceria: integrations
      .map((integration) => integration.ParceiroIntegracao?.codModalidadePeriodoGarantido)
      .filter((modalidade): modalidade is number => modalidade != null),
    altaTaxaCancelamento: member?.altaTaxaCancelamento ?? false,
    zendeskSessionToken: member?.zendeskSessionToken ?? "",
    funcionalidadesContratadas,
    pacotesServicos,
    especialidades: user?.especialidades ?? [],
    sub,
    ramalTroncoNormalizado,
    termoDeUsoAceitoMaisRecente: lastTermsOfUseAgreed,
    possivelDataFimTrialAssistPro,
    acoesPromocionais,
    possuiSitePersonalizado: hasCustomSite,
    possuiContaRecebedora: hasRecipientAccount,
    possuiAgendaAtiva: (userQuery?.usuariosAgendas ?? []).length > 0,
    possuiCartaoPendente,
    fluxosPendentes: (member?.fluxosPendentes ?? []) as FluxoPendenteDoUsuarioType[],
  };
};
